<script setup lang="ts">
import { ref } from "vue";
import type { FlyerSources } from "../eventAssets";

defineProps<{
  flyer: FlyerSources;
  eventName: string;
}>();

const dialogRef = ref<HTMLDialogElement | null>(null);

function open() {
  dialogRef.value?.showModal();
}
function close() {
  dialogRef.value?.close();
}
// Native <dialog>: clicks on the backdrop hit the dialog element itself,
// clicks on content hit a descendant — so this closes on backdrop only.
function onDialogClick(e: MouseEvent) {
  if (e.target === dialogRef.value) close();
}
</script>

<template>
  <div>
    <button
      type="button"
      class="group relative block w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
      data-testid="flyer-teaser"
      @click="open"
    >
      <!--
        Mobile: crop to a banner (object-top lands on the poster's title +
        date, which is the recognition cue). md+: the poster sits in its own
        column, so show it whole.
      -->
      <img
        :src="flyer.src600"
        :srcset="`${flyer.src600} 600w, ${flyer.src1200} 1200w`"
        sizes="(min-width: 768px) 24rem, calc(100vw - 2rem)"
        :alt="$t('buy.event.flyerAlt', { event: eventName })"
        class="h-80 w-full object-cover object-top md:h-auto md:object-contain"
        width="600"
        height="848"
      />
      <span
        class="absolute bottom-2 right-2 rounded-md bg-slate-900/70 px-2 py-1 text-xs font-medium text-white group-hover:bg-slate-900"
      >
        ⤢ {{ $t("buy.event.flyerOpen") }}
      </span>
    </button>

    <dialog
      ref="dialogRef"
      class="m-auto rounded-lg p-0 backdrop:bg-slate-900/70"
      data-testid="flyer-dialog"
      @click="onDialogClick"
    >
      <div class="relative">
        <img
          :src="flyer.src1200"
          :alt="$t('buy.event.flyerAlt', { event: eventName })"
          class="block max-h-[85vh] w-auto max-w-[calc(100vw-2rem)]"
          width="1200"
          height="1696"
        />
        <button
          type="button"
          class="absolute right-2 top-2 rounded-md bg-slate-900/70 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-white"
          data-testid="flyer-dialog-close"
          @click="close"
        >
          {{ $t("buy.event.flyerClose") }}
        </button>
      </div>
    </dialog>
  </div>
</template>
